import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { FraudPreventionService } from "@obg/ngx.common";
import {
	AuthTransactionAction,
	AuthTransactionConfigService,
	AuthTransactionSelector,
	CoreState,
	LoginMethod,
} from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { coerceBoolean, TelemetryService } from "@obg/ngx.global.evo";
import { ignoreNil } from "@obg/rxjs";
import { Observable } from "rxjs";

import { CommonAuthTelemetryEventName, CommonAuthTelemetryStartType } from "../../../sdk";
import { SgaLoginUiFlowService } from "../sga-login-flow.service";
import { SGA_LOGIN_TRANSACTION_PREFIX } from "../sga-login.const";
import { SgaLoginData } from "../sga-login.model";

@Component({
	selector: "obg-m-sga-login-container",
	templateUrl: "./sga-login.container.html",
	styleUrls: ["./sga-login.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	providers: [SgaLoginUiFlowService],
})
export class SgaLoginContainer implements OnInit {
	@HostBinding("class.obg-m-sga-login") hostCssClass = true;
	@HostBinding("class.quick-login") hostCssQuickLoginClass = false;

	@Input()
	@coerceBoolean()
	showLoginTitle = false;

	@Output() busy = new EventEmitter<boolean>();

	readonly translationPrefix = SGA_LOGIN_TRANSACTION_PREFIX;
	isQuickLoginEnabled = false;
	isQuickLoginMode = true;
	secureStartEnabled = false;
	secondaryButtonTranslationKey = `${this.translationPrefix}.another-device`;
	rememberPersonalNumber$: Observable<string> | undefined;

	constructor(
		private store: Store<CoreState>,
		private authTransactionAction: AuthTransactionAction,
		private authTransactionSelector: AuthTransactionSelector,
		private fraudPreventionService: FraudPreventionService,
		private telemetryService: TelemetryService,
		authTransactionConfig: AuthTransactionConfigService,
		deviceConfigService: DeviceConfigService
	) {
		this.isQuickLoginEnabled = this.hostCssQuickLoginClass =
			!deviceConfigService.isDesktop() && authTransactionConfig.getBankIdQuickLoginEnabled();
		this.secureStartEnabled = authTransactionConfig.getBankIdSecureStartEnabled();
	}

	ngOnInit() {
		this.fraudPreventionService.loadScript();
		this.rememberPersonalNumber$ = this.store.pipe(
			select(this.authTransactionSelector.getLastActivePersonalNumber()),
			ignoreNil()
		);
	}

	login(data?: SgaLoginData) {
		this.store.dispatch(
			this.authTransactionAction.initializeAuthTransaction(data ? data.personalNumberForm.value : undefined)
		);
	}

	switchDevice() {
		this.store.dispatch(this.authTransactionAction.reset());
		this.isQuickLoginMode = !this.isQuickLoginMode;
		this.secondaryButtonTranslationKey = this.isQuickLoginMode
			? `${this.translationPrefix}.another-device`
			: `${this.translationPrefix}.start`;
		if (this.isQuickLoginMode) {
			this.login();
		}

		this.switchDeviceGtmEventAction(this.isQuickLoginMode);
	}

	private switchDeviceGtmEventAction(isQuickLoginMode: boolean) {
		this.telemetryService.track({
			event: CommonAuthTelemetryEventName.BankIDStart,
			LoginMethod: LoginMethod.BankId,
			StartType: isQuickLoginMode
				? CommonAuthTelemetryStartType.SameDevice
				: CommonAuthTelemetryStartType.OtherDevice,
		});
	}
}
